import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { publicApiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import logo from "@/assets/connxt.png";
import logo_desktop from "@/assets/fynxt_blue.png";
import { useToast } from "@/components/common/Toast";
import { useAuthStore } from "@/store/auth.store";

const AcceptInvitation = () => {
	const { success, error } = useToast();
	const navigate = useNavigate();
	const resetAuth = useAuthStore((state) => state.resetAuth);
	const { token } = useParams();
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState({
		name: "",
		password: "",
		general: ""
	});
	const [formData, setFormData] = useState({
		name: "",
		password: ""
	});

	const validateForm = () => {
		const newErrors = {
			name: "",
			password: "",
			general: ""
		};

		if (!formData.name) {
			newErrors.name = "Name is required";
		}

		if (!formData.password) {
			newErrors.password = "Password is required";
		} else if (formData.password.length < 8) {
			newErrors.password = "Password must be at least 8 characters";
		}

		setErrors(newErrors);

		return !Object.values(newErrors).some((error) => error !== "");
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		setErrors((prev) => ({ ...prev, [name]: "", general: "" }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		if (!token) {
			setErrors((prev) => ({
				...prev,
				general: "Invalid invitation link"
			}));
			return;
		}

		setIsLoading(true);
		setErrors((prev) => ({ ...prev, general: "" }));

		try {
			await publicApiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["USER", "ACCEPT_INVITATION"], token),
				formData
			);
			success("Invitation accepted successfully");
			resetAuth();
			navigate("/login");
		} catch (_error) {
			setErrors((prev) => ({
				...prev,
				general: "Failed to accept invitation. Please try again."
			}));
			error("Failed to accept invitation. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex flex-col md:flex-row max-w-full w-full">
			<div className="w-full md:w-1/1 bg-[#002666]">
				<div className="flex items-center justify-center h-full">
					<img
						src={logo}
						alt="Logo"
						className="object-cover h-full max-w-full"
					/>
				</div>
			</div>

			<div className="w-full md:w-1/3 bg-white">
				<div className="flex min-h-full flex-col px-6 py-12 lg:px-8">
					<div className="sm:mx-auto sm:w-full sm:max-w-sm mt-24">
						<img
							src={logo_desktop}
							alt="Logo"
							className="object-cover h-full max-w-full sm:mx-auto"
						/>
						<h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-gray-900 mt-2">
							Accept Invitation
						</h2>
					</div>

					<div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
						<form className="space-y-6" onSubmit={handleSubmit} noValidate>
							<div>
								<label
									htmlFor="name"
									className="block text-sm font-medium text-gray-700 text-left mb-1"
								>
									Full Name
								</label>
								<input
									name="name"
									type="text"
									required
									aria-invalid={!!errors.name}
									aria-describedby={errors.name ? "name-error" : undefined}
									value={formData.name}
									onChange={handleChange}
									className={`block w-full rounded-md border-0 py-3 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
										errors.name ? "ring-red-500" : "ring-gray-300"
									} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
									placeholder="Enter your full name"
									disabled={isLoading}
								/>
								{errors.name && (
									<p className="mt-1 text-sm text-red-600" role="alert">
										{errors.name}
									</p>
								)}
							</div>

							<div>
								<label
									htmlFor="password"
									className="block text-sm font-medium text-gray-700 text-left mb-1"
								>
									Password
								</label>
								<input
									name="password"
									type="password"
									required
									aria-invalid={!!errors.password}
									aria-describedby={
										errors.password ? "password-error" : undefined
									}
									value={formData.password}
									onChange={handleChange}
									className={`block w-full rounded-md border-0 py-3 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
										errors.password ? "ring-red-500" : "ring-gray-300"
									} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
									placeholder="Enter your password"
									disabled={isLoading}
								/>
								{errors.password && (
									<p className="mt-1 text-sm text-red-600" role="alert">
										{errors.password}
									</p>
								)}
							</div>

							{errors.general && (
								<div className="rounded-md bg-red-50 p-4">
									<p className="text-sm text-red-700" role="alert">
										{errors.general}
									</p>
								</div>
							)}

							<div className="flex flex-col gap-4">
								<button
									type="submit"
									className="flex w-full justify-center rounded-md bg-[#002666] px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
									disabled={isLoading}
									aria-busy={isLoading}
								>
									{isLoading ? (
										<output className="flex items-center">
											<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
											<span>Processing...</span>
										</output>
									) : (
										"Accept Invitation"
									)}
								</button>
								<div className="relative">
									<div className="absolute inset-0 flex items-center">
										<div className="w-full border-t border-gray-300" />
									</div>
									<div className="relative flex justify-center text-sm">
										<span className="bg-white px-2 text-gray-500">or</span>
									</div>
								</div>
								<button
									type="button"
									onClick={() => navigate("/login")}
									className="text-center text-sm font-medium text-indigo-600 hover:text-indigo-500 rounded-md"
								>
									Back to Sign In
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AcceptInvitation;
